"use client";

import { InsightsGenuiAbstainView } from "./InsightsGenuiAbstain";
import { InsightsGenuiBar } from "./InsightsGenuiBar";
import { InsightsGenuiBento } from "./InsightsGenuiBento";
import { InsightsGenuiTable } from "./InsightsGenuiTable";
import { InsightsLedgerCard } from "./InsightsLedgerCard";
import { InsightsMessageText } from "./InsightsMessageText";
import { InsightsRenderFrame } from "./InsightsRenderFrame";
import { InsightsRenderSkeleton } from "./InsightsRenderSkeleton";
import {
  looksLikeHtmlFragment,
  inferRenderWidgetKind,
  tryParseGenuiSpecStreaming,
} from "./parse-genui";
import { parseInsightsAnswerSegments } from "./parse-render-blocks";
import { prepareAnswerTextForDisplay } from "./prepare-answer-display";

/**
 * Renders assistant answer prose + <ledger> + <render> segments.
 * Callers: InsightsChat final-answer row, InsightsTaskSection / InsightsPhaseSection
 * assistant-prose children.
 *
 * Structured finals: when any <render> is GenUI type:bento or type:abstain, that
 * document IS the answer — prose beside it is suppressed (ledgers still render).
 * Incomplete <render>: progressive GenUI fill via tryParseGenuiSpecStreaming;
 * HTML uses typed InsightsRenderSkeleton until </render>.
 */
export function InsightsAnswer({
  text,
  streaming,
  evidenceLine,
}: {
  text: string;
  streaming?: boolean;
  evidenceLine?: string | null;
}) {
  const segments = parseInsightsAnswerSegments(prepareAnswerTextForDisplay(text));

  const structuredIndexes = new Set<number>();
  let hasBento = false;
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i]!;
    if (seg.type !== "render") continue;
    const genui = tryParseGenuiSpecStreaming(seg.html);
    if (genui?.type === "bento" || genui?.type === "abstain") {
      structuredIndexes.add(i);
      if (genui.type === "bento") hasBento = true;
    }
  }
  const structuredMode = structuredIndexes.size > 0;

  return (
    <div className={hasBento ? "space-y-0" : "space-y-4 p-4"}>
      {segments.map((seg, i) => {
        if (seg.type === "ledger") {
          return <InsightsLedgerCard key={`l-${i}`} content={seg.content} live={!seg.complete} />;
        }

        if (structuredMode) {
          if (seg.type === "prose") return null;
          if (seg.type === "render" && !structuredIndexes.has(i)) return null;
        }

        if (seg.type === "prose") {
          if (!seg.text.trim()) return null;
          return <InsightsMessageText key={`p-${i}`} text={seg.text} />;
        }

        const live = !seg.complete;
        const genui = tryParseGenuiSpecStreaming(seg.html);
        if (genui?.type === "bento") {
          return <InsightsGenuiBento key={`b-${i}`} spec={genui} live={live} />;
        }
        if (genui?.type === "abstain") {
          return <InsightsGenuiAbstainView key={`a-${i}`} spec={genui} live={live} />;
        }
        if (genui?.type === "table") {
          return <InsightsGenuiTable key={`g-${i}`} spec={genui} live={live} />;
        }
        if (genui?.type === "bar") {
          return <InsightsGenuiBar key={`g-${i}`} spec={genui} live={live} />;
        }

        if (live) {
          return <InsightsRenderSkeleton key={`s-${i}`} kind={inferRenderWidgetKind(seg.html)} />;
        }
        if (looksLikeHtmlFragment(seg.html) || seg.html.trim()) {
          return <InsightsRenderFrame key={`r-${i}`} html={seg.html} />;
        }
        return null;
      })}
      {evidenceLine && !structuredMode ? (
        <p className="text-[12px] leading-5 text-foreground/55">{evidenceLine}</p>
      ) : null}
      {streaming ? (
        <span className="inline-block h-4 w-1.5 animate-pulse rounded-sm bg-foreground/40 align-middle" />
      ) : null}
    </div>
  );
}
